/**
 * Pilot behaviour: validates an instruction against the aircraft's situation (ATC-SIM
 * rules), applies it to the nav state, and produces the ATC transmission and the readback.
 * Timing (compliance delay, wrong readbacks) is handled by the sim loop, not here.
 */
import { bearingDeg, crossTrackM, defaultApproach, distanceM, findRunwayEnd, NM_TO_M, toTrueDeg, wrap180, type AirportData, type RealismOptions, type Restriction, type RouteLeg } from '@atc/core';
import { applyGoAround, clampIas, isAwaitingDeparture, isOnGroundPhase, isRemovedPhase, publishedHoldState, setPhase, type Aircraft, type AircraftEvent, type HoldState } from './aircraft';
import { altitudeSpeech, altitudeText, atcPhrase, callsignPhrase, fixSpeech, headingSpeech, headingText, pilotPhrase, runwaySpeech, speedSpeech, type Phrase } from './comms';
import type { Instruction } from './commands';

export interface PilotResponse {
  ok: boolean;
  /** One-line confirmation or rejection for the command area (ATC-SIM style). */
  message: string;
  atc: Phrase;
  pilot: Phrase;
}

/** What earlier instructions in the same transmission have already set up. */
export interface InstructionPlan {
  /** Heading an earlier heading or direct instruction turns onto. */
  headingDeg?: number;
}

/**
 * Check whether the aircraft can accept the instruction now. Returns a rejection reason or null.
 * `plan` carries what the same transmission set up earlier, so "C 250 L 28R" is judged on
 * heading 250 and "C 5 T" counts the altitude as assigned.
 */
export function checkInstruction(ac: Aircraft, instr: Instruction, airport: AirportData, plan: InstructionPlan = {}): string | null {
  const ground = isOnGroundPhase(ac.phase);
  // Departures waiting for take-off accept the heading, fix and speed to fly once airborne.
  const groundUnable = ground && !isAwaitingDeparture(ac.phase);
  switch (instr.kind) {
    case 'altitude':
      if (instr.altitudeFt < airport.elevationFt + 500) return `altitude ${altitudeText(instr.altitudeFt)} is below the field`;
      return null;
    case 'heading':
      if (groundUnable) return 'on the ground';
      return null;
    case 'direct':
      if (groundUnable) return 'on the ground';
      if (!airport.fixes.has(instr.fix)) return `unknown fix ${instr.fix}`;
      return null;
    case 'hold':
      if (ground) return 'on the ground';
      if (!airport.fixes.has(instr.fix)) return `unknown fix ${instr.fix}`;
      return null;
    case 'speed': {
      if (groundUnable) return 'on the ground';
      const limit = clampIas(ac, instr.speedKt);
      if (limit !== instr.speedKt) return `${instr.speedKt < limit ? 'minimum' : 'maximum'} speed ${limit} knots`;
      return null;
    }
    case 'land': case 'approach': {
      // An approach clearance is issued while the aircraft is still being vectored and descending,
      // so there is no altitude or heading limit on accepting one. What follows is flown, not
      // refused: an aircraft that never captures the localizer reports "did not intercept, request
      // vectors", and one that cannot get down onto the glideslope goes around (aircraft.ts).
      if (!findRunwayEnd(airport, instr.runway)) return `no runway ${instr.runway} at ${airport.icao}`;
      if (ground) return 'on the ground';
      return null;
    }
    case 'takeoff':
      if (ac.phase === 'takeoffRoll') return null;
      if (!ground || !isAwaitingDeparture(ac.phase)) return 'not holding for departure';
      return null;
    case 'lineUp':
      if (ac.phase === 'lineUp' || ac.phase === 'linedUp') return null;
      if (ac.phase !== 'holdShort') return 'not holding short';
      return null;
    case 'abort':
      // Anything airborne can be sent around, approach clearance or not: after a vector cancelled
      // the approach, a second time during a go-around, or simply because the controller wants the
      // aircraft off the final. Only an aircraft on the ground has nothing to go around from.
      if (ac.phase === 'takeoffRoll') return null;
      if (ac.phase === 'abortRoll') return null;
      if (ac.phase === 'rollout' || ac.phase === 'vacating') return 'already on the ground';
      if (isAwaitingDeparture(ac.phase)) return 'still on the ground';
      if (ground || isRemovedPhase(ac.phase)) return 'nothing to abort';
      return null;
    case 'resumeNav':
      if (groundUnable) return 'on the ground';
      if (!ac.flightPlan.some((l) => l.kind === 'fix' && airport.fixes.has(l.fix))) return 'no filed route to resume';
      return null;
    case 'contact': {
      if (ac.kind !== 'departure') return 'we are inbound to you';
      if (ground) return 'still on the ground';
      const agl = ac.altitudeFt - airport.elevationFt;
      if (agl < 2000) return `only ${Math.max(0, Math.round(agl / 100) * 100)} feet above the field`;
      const fix = airport.fixes.get(ac.requested);
      // Flying its SID towards the exit fix is on course, whichever leg it is on now.
      const onSid = plan.headingDeg === undefined && ac.nav.mode === 'route' && (ac.nav.route ?? []).slice(ac.nav.routeIdx).some((l) => l.kind === 'fix' && l.fix === ac.requested);
      if (!onSid && fix && distanceM(ac.lat, ac.lon, fix.lat, fix.lon) > 2 * NM_TO_M && Math.abs(wrap180(bearingDeg(ac.lat, ac.lon, fix.lat, fix.lon) - (plan.headingDeg ?? ac.nav.headingDeg))) > 45) return `not yet on course to ${ac.requested}`;
      return null;
    }
  }
}

/**
 * The fixes a crew flies when cleared for an approach on its way to a fix of the published
 * procedure (direct to it, along its route to it, or holding at it): the rest of the way there,
 * then the procedure's fixes, each with the lowest altitude allowed at it. Undefined when the
 * aircraft is on a heading, or its way ahead never reaches the procedure: then it flies its heading
 * to the localizer, as vectored.
 */
export function procedureVia(ac: Aircraft, airport: AirportData, runway: string): { fix: string; altitudeFt?: number }[] | undefined {
  const approach = defaultApproach(airport, runway);
  if (!approach || approach.source === 'synthesized') return undefined;
  const nav = ac.nav;
  const directIdx = nav.mode === 'direct' ? ac.flightPlan.findIndex((l) => l.kind === 'fix' && l.fix === nav.fix) : -1;
  const ahead: RouteLeg[] = nav.mode === 'route' ? (nav.route ?? []).slice(nav.routeIdx)
    : nav.mode === 'direct' && nav.fix ? [{ kind: 'fix', fix: nav.fix }, ...(directIdx >= 0 ? ac.flightPlan.slice(directIdx + 1) : [])]
      : nav.mode === 'hold' && nav.hold ? [{ kind: 'fix', fix: nav.hold.fix }] : [];
  const lowest = (r?: Restriction) => r?.altitudeFt ?? r?.altitudeMinFt;
  const names = approach.legs.map((l) => l.fix);
  const out: { fix: string; altitudeFt?: number }[] = [];
  for (const leg of ahead) {
    if (leg.kind !== 'fix') return undefined;
    const k = names.indexOf(leg.fix);
    if (k >= 0) return [...out, ...approach.legs.slice(k).map((l) => ({ fix: l.fix, altitudeFt: lowest(l.restriction) }))];
    out.push({ fix: leg.fix, altitudeFt: lowest(leg.restriction) });
  }
  return undefined;
}

/** Where "resume own navigation" rejoins the flight plan: the nearest fix, or the one after it if that is already behind. */
export function rejoinIndex(ac: Aircraft, airport: AirportData): number {
  const fixes = ac.flightPlan.flatMap((l, i) => { const f = l.kind === 'fix' ? airport.fixes.get(l.fix) : undefined; return f ? [{ i, f }] : []; });
  if (!fixes.length) return 0;
  const from = (f: { lat: number; lon: number }) => distanceM(ac.lat, ac.lon, f.lat, f.lon);
  const best = fixes.reduce((a, b) => (from(b.f) < from(a.f) ? b : a));
  const next = fixes.find((x) => x.i > best.i);
  return next && from(next.f) < distanceM(best.f.lat, best.f.lon, next.f.lat, next.f.lon) ? next.i : best.i;
}

/**
 * Re-check a delayed instruction when it finally runs. A landing clearance that was read back
 * is flown whatever the heading is by then (a bad intercept is caught by the localizer logic),
 * so only the runway and the ground state are checked for it.
 */
export function recheckInstruction(ac: Aircraft, instr: Instruction, airport: AirportData): string | null {
  if (instr.kind !== 'land' && instr.kind !== 'approach') return checkInstruction(ac, instr, airport);
  if (!findRunwayEnd(airport, instr.runway)) return `no runway ${instr.runway} at ${airport.icao}`;
  return isOnGroundPhase(ac.phase) ? 'on the ground' : null;
}

export type InstructionAxis = 'lateral' | 'vertical' | 'speed' | 'ground' | 'frequency';

/** Which part of the flight path an instruction controls; a newer instruction on the same axis replaces a queued one. */
export function instructionAxis(instr: Instruction): InstructionAxis {
  switch (instr.kind) {
    case 'altitude': return 'vertical';
    case 'speed': return 'speed';
    case 'takeoff': case 'lineUp': return 'ground';
    case 'contact': return 'frequency';
    default: return 'lateral';
  }
}

/** What else the transmission being flown contains. */
export interface ExecuteContext {
  /** The transmission assigns an altitude, so cancelling an approach must not level off. */
  altitudeInTransmission?: boolean;
  /** The transmission assigns a speed, so cancelling an approach must keep it. */
  speedInTransmission?: boolean;
}

/** Apply an already-checked instruction. Returns an event when the instruction starts a go-around. */
export function executeInstruction(ac: Aircraft, instr: Instruction, airport: AirportData, opts: RealismOptions, ctx: ExecuteContext = {}): AircraftEvent | null {
  void opts;
  switch (instr.kind) {
    case 'altitude':
      ac.targetAltitudeFt = instr.altitudeFt;
      ac.expedite = instr.expedite && !isOnGroundPhase(ac.phase);
      ac.assigned.altitudeFt = instr.altitudeFt;
      ac.assigned.expedite = ac.expedite;
      break;
    case 'heading':
      cancelApproach(ac, ctx);
      // Said magnetic, flown true; the assigned heading keeps what was said.
      ac.nav = { mode: 'heading', headingDeg: toTrueDeg(airport, instr.headingDeg), turn: instr.turn, routeIdx: 0 };
      ac.assigned = { ...ac.assigned, headingDeg: instr.headingDeg, turn: instr.turn, fix: undefined, hold: undefined };
      break;
    case 'direct':
      cancelApproach(ac, ctx);
      ac.nav = { mode: 'direct', headingDeg: ac.headingDeg, fix: instr.fix, turn: instr.turn, routeIdx: 0 };
      ac.assigned = { ...ac.assigned, fix: instr.fix, turn: instr.turn, headingDeg: undefined, hold: undefined };
      break;
    case 'hold': {
      cancelApproach(ac, ctx);
      const fix = airport.fixes.get(instr.fix)!;
      const hold: HoldState = publishedHoldState(airport, instr.fix, 'toFix') ?? { fix: instr.fix, inboundDeg: bearingDeg(ac.lat, ac.lon, fix.lat, fix.lon), legS: 60, stage: 'toFix', t: 0 };
      ac.nav = { mode: 'hold', headingDeg: ac.headingDeg, routeIdx: 0, hold };
      ac.assigned = { ...ac.assigned, hold: instr.fix, fix: undefined, headingDeg: undefined };
      break;
    }
    case 'speed':
      ac.targetIasKt = clampIas(ac, instr.speedKt);
      ac.assigned.speedKt = ac.targetIasKt;
      break;
    case 'land': case 'approach': {
      const rw = findRunwayEnd(airport, instr.runway)!;
      ac.arrivalRunway = rw;
      if (ac.approach?.runway.name === rw.name) {
        // Already on this approach: a landing clearance changes nothing else.
        if (instr.kind === 'land') ac.approach.cleared = true;
        ac.assigned = { ...ac.assigned, runway: rw.name };
        break;
      }
      const via = procedureVia(ac, airport, rw.name);
      ac.approach = { runway: rw, established: false, onSlope: false, missed: false, cleared: instr.kind === 'land', clearedAtNm: distanceM(ac.lat, ac.lon, rw.lat, rw.lon) / NM_TO_M, clearedSide: Math.sign(crossTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon)), ...(via ? { via } : {}) };
      // Keep flying an assigned heading (and finish the turn onto it) until the localizer is
      // captured; on the way to the procedure, fly it from the fix ahead.
      const intercept = ac.nav.mode === 'heading' ? { headingDeg: ac.nav.headingDeg, turn: ac.nav.turn } : { headingDeg: ac.headingDeg, turn: via ? ac.nav.turn : undefined };
      ac.nav = { mode: 'localizer', ...intercept, routeIdx: 0 };
      ac.assigned = { ...ac.assigned, runway: rw.name, hold: undefined, fix: undefined };
      setPhase(ac, 'approach');
      break;
    }
    case 'takeoff':
      if (ac.phase === 'takeoffRoll') break;
      // The departure can roll without an assigned altitude. It climbs to a limited
      // procedural height and asks for a clearance once airborne.
      if (ac.assigned.altitudeFt === undefined) ac.targetAltitudeFt = Math.max(ac.targetAltitudeFt, airport.elevationFt + 1000);
      if (ac.phase === 'linedUp') setPhase(ac, 'takeoffRoll');
      else { ac.rollAfterLineUp = true; if (ac.phase === 'holdShort') setPhase(ac, 'lineUp'); }
      ac.holdPosition = false;
      break;
    case 'lineUp':
      if (ac.phase === 'holdShort') setPhase(ac, 'lineUp');
      break;
    case 'abort':
      // Go-around by instruction: the same state change as the automatic one, announced by the
      // controller. With no approach to cancel it is a climb away on the present heading.
      if (ac.phase === 'takeoffRoll') setPhase(ac, 'abortRoll');
      else if (ac.phase === 'abortRoll') break;
      else return applyGoAround(ac, airport, 'instructed', 'controller');
      break;
    case 'resumeNav': {
      cancelApproach(ac, ctx);
      ac.nav = { mode: 'route', headingDeg: ac.headingDeg, route: ac.flightPlan, routeIdx: rejoinIndex(ac, airport) };
      ac.assigned = { ...ac.assigned, fix: undefined, headingDeg: undefined, turn: undefined, hold: undefined };
      break;
    }
    case 'contact':
      setPhase(ac, 'handedOff');
      return { type: 'handoff', ac, fix: ac.requested, manual: true };
  }
  return null;
}

function cancelApproach(ac: Aircraft, ctx: ExecuteContext): void {
  if (!ac.approach) return;
  ac.approach = undefined;
  ac.assigned.runway = undefined;
  // Level off where we are (rounded to 100 ft) unless the same transmission assigns an altitude.
  if (!ctx.altitudeInTransmission) { ac.targetAltitudeFt = Math.round(ac.altitudeFt / 100) * 100; ac.assigned.altitudeFt = ac.targetAltitudeFt; }
  if (!ctx.speedInTransmission) { ac.targetIasKt = null; ac.assigned.speedKt = undefined; }
  if (ac.phase === 'approach' || ac.phase === 'final') setPhase(ac, 'enroute');
}

/** ATC transmission and pilot readback for an instruction (already checked). */
/** `farewell` is the pilot's sign-off on a handoff readback; the canonical one is "good day". */
export function phraseInstruction(ac: Aircraft, instr: Instruction, airport: AirportData, telephony?: Record<string, string>, farewell = 'good day'): { atc: Phrase; pilot: Phrase; message: string } {
  const cs = callsignPhrase(ac, telephony);
  let body: Phrase, read: Phrase, message: string;
  switch (instr.kind) {
    case 'altitude': {
      const ground = isOnGroundPhase(ac.phase);
      const verb = ground ? 'after departure climb and maintain' : instr.altitudeFt > ac.altitudeFt ? 'climb and maintain' : 'descend and maintain';
      const ex = instr.expedite && !ground ? ', expedite' : '';
      body = { text: `${verb} ${altitudeText(instr.altitudeFt)}${ex}`, speech: `${verb} ${altitudeSpeech(instr.altitudeFt)}${ex}` };
      read = ground ? { text: `${altitudeText(instr.altitudeFt)} after departure`, speech: `${altitudeSpeech(instr.altitudeFt)} after departure` } : { text: `${verb.split(' ')[0]} and maintain ${altitudeText(instr.altitudeFt)}${ex}`, speech: `${verb.split(' ')[0]} and maintain ${altitudeSpeech(instr.altitudeFt)}${ex}` };
      message = `cleared to ${altitudeText(instr.altitudeFt)} ft${instr.expedite ? ', expedite' : ''}`;
      break;
    }
    case 'heading': {
      if (isAwaitingDeparture(ac.phase)) {
        const turn = instr.turn === 'L' ? 'turn left ' : instr.turn === 'R' ? 'turn right ' : 'fly ';
        body = { text: `after departure ${turn}heading ${headingText(instr.headingDeg)}`, speech: `after departure ${turn}heading ${headingSpeech(instr.headingDeg)}` };
        read = { text: `heading ${headingText(instr.headingDeg)} after departure`, speech: `heading ${headingSpeech(instr.headingDeg)} after departure` };
        message = `after departure heading ${headingText(instr.headingDeg)}`;
        break;
      }
      const dir = instr.turn === 'L' ? 'left' : instr.turn === 'R' ? 'right' : wrap180(toTrueDeg(airport, instr.headingDeg) - ac.headingDeg) < 0 ? 'left' : 'right';
      body = { text: `turn ${dir} heading ${headingText(instr.headingDeg)}`, speech: `turn ${dir} heading ${headingSpeech(instr.headingDeg)}` };
      read = { text: `${dir} heading ${headingText(instr.headingDeg)}`, speech: `${dir} heading ${headingSpeech(instr.headingDeg)}` };
      message = `turn ${dir} to heading ${headingText(instr.headingDeg)}`;
      break;
    }
    case 'direct': {
      const dir = instr.turn === 'L' ? 'turn left, ' : instr.turn === 'R' ? 'turn right, ' : '';
      const after = isAwaitingDeparture(ac.phase);
      body = { text: `${after ? 'after departure ' : ''}${dir}proceed direct ${instr.fix}`, speech: `${after ? 'after departure ' : ''}${dir}proceed direct ${fixSpeech(instr.fix)}` };
      read = { text: `${dir}direct ${instr.fix}${after ? ' after departure' : ''}`, speech: `${dir}direct ${fixSpeech(instr.fix)}${after ? ' after departure' : ''}` };
      message = `${after ? 'after departure direct' : 'cleared direct'} ${instr.fix}`;
      break;
    }
    case 'hold':
      body = { text: `hold at ${instr.fix}`, speech: `hold at ${fixSpeech(instr.fix)}` };
      read = { text: `hold at ${instr.fix}`, speech: `hold at ${fixSpeech(instr.fix)}` };
      message = `hold at ${instr.fix}`;
      break;
    case 'speed': {
      if (isAwaitingDeparture(ac.phase)) {
        body = { text: `after departure maintain ${instr.speedKt} knots`, speech: `after departure maintain ${speedSpeech(instr.speedKt)}` };
        read = { text: `${instr.speedKt} knots after departure`, speech: `${speedSpeech(instr.speedKt)} after departure` };
        message = `after departure speed ${instr.speedKt} kt`;
        break;
      }
      const verb = instr.speedKt < ac.iasKt ? 'reduce speed to' : 'increase speed to';
      body = { text: `${verb} ${instr.speedKt} knots`, speech: `${verb} ${speedSpeech(instr.speedKt)}` };
      read = { text: `speed ${instr.speedKt} knots`, speech: `speed ${speedSpeech(instr.speedKt)}` };
      message = `speed ${instr.speedKt} kt`;
      break;
    }
    case 'land': {
      const name = findRunwayEnd(airport, instr.runway)?.name ?? instr.runway;
      body = { text: `cleared to land runway ${name}`, speech: `cleared to land runway ${runwaySpeech(name)}` };
      read = { text: `cleared to land runway ${name}`, speech: `cleared to land runway ${runwaySpeech(name)}` };
      message = `cleared to land runway ${name}`;
      break;
    }
    case 'takeoff': {
      const rw = ac.departureRunway?.name ?? '';
      body = { text: `runway ${rw}, cleared for takeoff`, speech: `runway ${runwaySpeech(rw)}, cleared for takeoff` };
      read = { text: `cleared for takeoff runway ${rw}`, speech: `cleared for takeoff runway ${runwaySpeech(rw)}` };
      message = `cleared for takeoff runway ${rw}`;
      break;
    }
    case 'lineUp': {
      const rw = ac.departureRunway?.name ?? '';
      body = { text: `runway ${rw}, line up and wait`, speech: `runway ${runwaySpeech(rw)}, line up and wait` };
      read = { text: `line up and wait runway ${rw}`, speech: `line up and wait runway ${runwaySpeech(rw)}` };
      message = `line up and wait runway ${rw}`;
      break;
    }
    case 'approach': {
      const name = findRunwayEnd(airport, instr.runway)?.name ?? instr.runway;
      body = { text: `cleared ILS approach runway ${name}`, speech: `cleared I L S approach runway ${runwaySpeech(name)}` };
      read = body;
      message = `cleared ILS approach runway ${name}`;
      break;
    }
    case 'resumeNav':
      body = { text: 'resume own navigation', speech: 'resume own navigation' };
      read = { text: 'resuming own navigation', speech: 'resuming own navigation' };
      message = 'resume own navigation';
      break;
    case 'contact':
      body = { text: `contact ${airport.radio.dep}, good day`, speech: `contact ${airport.radio.dep}, good day` };
      read = { text: `${airport.radio.dep}, ${farewell}`, speech: `${airport.radio.dep}, ${farewell}` };
      message = `handed off to ${airport.radio.dep}`;
      break;
    case 'abort':
      if (ac.phase === 'takeoffRoll') { body = { text: 'abort takeoff', speech: 'abort takeoff' }; read = { text: 'aborting', speech: 'aborting' }; message = 'aborting takeoff'; }
      else { body = { text: 'go around', speech: 'go around' }; read = { text: 'going around', speech: 'going around' }; message = 'going around'; }
      break;
  }
  return { atc: atcPhrase(cs, body), pilot: pilotPhrase(read, cs), message: `${ac.callsign}: ${message}` };
}

export function unablePhrase(ac: Aircraft, reason: string, telephony?: Record<string, string>): Phrase {
  const cs = callsignPhrase(ac, telephony);
  return pilotPhrase({ text: `unable, ${reason}`, speech: `unable, ${reason.replace(/\d+°/g, (m) => m.replace('°', ' degrees'))}` }, cs);
}

/** Distance in nm to the fix the aircraft is heading to, for strips. */
export function distanceToFixNm(ac: Aircraft, airport: AirportData, fix: string): number | undefined {
  const f = airport.fixes.get(fix);
  return f ? distanceM(ac.lat, ac.lon, f.lat, f.lon) / 1852 : undefined;
}
